import { useState } from "react";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { SupplierRecordView } from "@shared-core/data/purchase-ledger-types";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../primitives/dialog";
import { Textarea } from "../../primitives/textarea";

/**
 * Put a supplier on hold, or release the hold. Either way asks why, and the
 * ledger records the reason against the person. A hold stops purchase invoices being
 * released for payment; it does not stop matching or new purchase orders
 * (Ben, 3 Oct). Offered only when the account carries the step's URL (a closed
 * supplier has nothing to hold) and the caller may manage suppliers.
 */
export function SupplierHoldButton({
  account,
  onChanged,
}: {
  account: SupplierRecordView;
  /** The account as it now stands, re-read after the hold or release. */
  onChanged: (account: SupplierRecordView) => void;
}) {
  const adapter = useDataAdapter();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isOnHold = account.status === "on_hold";
  const offered = isOnHold ? account.links.releaseHold : account.links.hold;
  if (!offered || !account.canManage) return null;

  const label = isOnHold ? "Release hold" : "Put on hold";
  const description = isOnHold
    ? `Purchase invoices from ${account.name} can be released for payment again once confirmed.`
    : `Purchase invoices from ${account.name} can still be matched and confirmed, but none is released for payment until the hold is released. New purchase orders can still be placed.`;

  async function submit() {
    setSaving(true);
    setError(null);
    try {
      const input = { reason: reason.trim() };
      const updated = isOnHold
        ? await adapter.releaseSupplierHold(account, input)
        : await adapter.placeSupplierOnHold(account, input);
      toast.success(
        isOnHold
          ? `${account.name}'s hold released`
          : `${account.name} put on hold`,
      );
      setOpen(false);
      setReason("");
      onChanged(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : `${label} failed.`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setReason("");
          setError(null);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          {label}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{label}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={
            isOnHold
              ? "e.g. Credit note CRN-PPG-0192 received for the short delivery"
              : "e.g. Disputed short delivery on PPG-71204 — awaiting a credit"
          }
          aria-label="Reason"
          rows={3}
        />
        <p className="text-muted-foreground text-xs">
          Recorded against your name on the supplier's history.
        </p>
        {error && (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        )}
        <Button disabled={reason.trim() === "" || saving} onClick={submit}>
          {label}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
